import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DangerousIcon from '@mui/icons-material/Dangerous';
import FlagIcon from '@mui/icons-material/Flag';
import VerifiedIcon from '@mui/icons-material/Verified';
import GppGoodIcon from '@mui/icons-material/GppGood';
import SmartToyIcon from '@mui/icons-material/SmartToy'; // Robot for AI
import InfoIcon from '@mui/icons-material/Info';
import './VerificationPage.css';

// --- Helper to determine context (AI Dominant, FN Dominant, Mixed) ---
const getDominanceContext = (scoreType, aiScore = 0, fnScore = 0) => {
    // 1. Meta score logic
    if (scoreType === 'digital_integrity_score') {
        const THRESHOLD = 50;
        if (aiScore >= THRESHOLD && fnScore >= THRESHOLD) return 'mixed';
        if (fnScore > aiScore) return 'fn_dominant';
        if (aiScore > fnScore) return 'ai_dominant';
        return 'neutral';
    }

    // 2. Informational Tasks (AI)
    if (scoreType === 'ai_detection') return 'ai_dominant';
    
    // 3. Risk Tasks (Fake News, Forgery, Scam, Brand Safety, PII)
    // Default to fn_dominant which uses the Red/Orange Risk scale
    return 'fn_dominant'; 
};

// --- Profile Logic: Determines Colors and Icons ---
const getRiskProfile = (riskScore, contextKey) => {
    // 1. AI Context (Informative, not "Danger")
    if (contextKey === 'ai_dominant') {
        if (riskScore >= 90) return { levelKey: 'severe', color: '#F44336', Icon: SmartToyIcon }; // Red + Robot
        if (riskScore >= 75) return { levelKey: 'high', color: '#FF9800', Icon: SmartToyIcon }; // Orange + Robot
        if (riskScore >= 50) return { levelKey: 'moderate', color: '#FFC107', Icon: FlagIcon }; // Amber + Robot
        if (riskScore >= 25) return { levelKey: 'minor', color: '#BBC34a', Icon: CheckCircleIcon };
        if (riskScore > 10) return { levelKey: 'low', color: '#8BC34A', Icon: VerifiedIcon };
        return { levelKey: 'minimal', color: '#4CAF50', Icon: GppGoodIcon };
    }

    // 2. Fake News / Mixed Context (Risk/Danger)
    if (riskScore >= 90) return { levelKey: 'severe', color: '#F44336', Icon: DangerousIcon }; // Red + Dangerous
    if (riskScore >= 75) return { levelKey: 'high', color: '#FF9800', Icon: WarningIcon };
    if (riskScore >= 50) return { levelKey: 'moderate', color: '#FFC107', Icon: FlagIcon };
    if (riskScore >= 25) return { levelKey: 'minor', color: '#8BC34A', Icon: CheckCircleIcon };
    if (riskScore > 10) return { levelKey: 'low', color: '#8BC34A', Icon: VerifiedIcon };
    return { levelKey: 'minimal', color: '#4CAF50', Icon: GppGoodIcon };
};

const ScoreDonut = ({ riskScore, scoreType, subScores = {}, size = 160, compact = false, showPercentSymbol = true }) => {
    const { t } = useTranslation();
    const safeRiskScore = riskScore != null ? riskScore : 0;

    // 1. Determine Context
    const aiScore = subScores?.ai_detection?.score || subScores?.ai || 0;
    const fnScore = subScores?.fake_news_detection?.score || subScores?.fn || 0;
    const contextKey = getDominanceContext(scoreType, aiScore, fnScore);

    // 2. Get Visual Profile (Color/Icon)
    const { levelKey, color, Icon } = getRiskProfile(safeRiskScore, contextKey);

    // 3. Determine the Label Text (Restoring the 6 buckets logic)
    // We look up a specific label based on the Context (AI/FN) and Level (Severe/High/etc)
    // Example key: verificationPage.donut.levels.ai_dominant.severe -> "Fully Synthetic"
    const integrityLabel = t(`verificationPage.donut.levels.${contextKey}.${levelKey}`, { 
        defaultValue: t(`riskLevels.${levelKey}`) // Fallback to generic "Severe", "High", etc.
    });

    // 4. Sub-label (e.g. LIKELIHOOD OF...)
    const getSubLabel = () => {
        if (scoreType === 'digital_integrity_score') return t('verificationPage.donut.labelRisk').toUpperCase();
        if (scoreType === 'ai_detection') return t('verificationPage.donut.labelAi').toUpperCase();
        if (scoreType === 'fake_news_detection') return t('verificationPage.donut.labelFakeNews').toUpperCase();
        // Dynamic Fallback for new tasks
        const dynamicName = t(`verificationPage.analysisTasks.${scoreType}`, { defaultValue: scoreType.replace(/_/g, ' ') });
        return `LIKELIHOOD OF ${dynamicName}`.toUpperCase();
    };

    // SVG calculations
    const radius = size / 2;
    const stroke = size / 13.33;
    const normalizedRadius = radius - stroke;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (safeRiskScore / 100) * circumference;

    const percentageFontSize = compact ? size * 0.28 : size * 0.22;
    const levelFontSize = size * 0.1;
    const subLabelFontSize = size * 0.07;
    const iconSize = size * 0.12;

    return (
        <div className="score-donut-container">
            <div className="score-donut-visual-wrapper" style={{ width: size, height: size }}>
                <svg height={size} width={size} className="score-donut-svg">
                    <circle
                        className="score-donut-background"
                        strokeWidth={stroke}
                        r={normalizedRadius}
                        cx={radius}
                        cy={radius}
                    />
                    <circle
                        className="score-donut-progress"
                        strokeWidth={stroke}
                        strokeDasharray={circumference + ' ' + circumference}
                        style={{ strokeDashoffset, stroke: color }}
                        r={normalizedRadius}
                        cx={radius}
                        cy={radius}
                        strokeLinecap="butt"
                    />
                </svg>
                <span className="score-donut-percentage" style={{ fontSize: `${percentageFontSize}px`, color: color }}>
                    {safeRiskScore}{showPercentSymbol ? '%' : ''}
                </span>
            </div>
            {!compact && (
                <div className="score-donut-text-below">
                    <div className="score-donut-label-wrapper">
                        <span className="score-donut-level" style={{ color, fontSize: `${levelFontSize}px` }}>
                            {integrityLabel}
                        </span>
                        <Icon className="integrity-icon" style={{ color, fontSize: `${iconSize}px` }} />
                    </div>
                    <div className="score-donut-label-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '4px' }}>
                        <span className="score-donut-sub-label" style={{ fontSize: `${subLabelFontSize}px` }}>
                            {getSubLabel()}
                        </span>
                        <Tooltip
                            title={t(`verificationPage.scoreDefinitions.${scoreType}`)}
                            arrow
                            placement="top"
                            PopperProps={{ className: 'global-dark-tooltip' }}
                        >
                            <InfoIcon
                                sx={{
                                    fontSize: '0.9rem',
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    cursor: 'help',
                                    '&:hover': { color: 'white' }
                                }}
                            />
                        </Tooltip>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScoreDonut;